import React from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * @param {{ page: number, pages: number, total: number, limit: number, onPageChange: (p: number) => void, onLimitChange?: (n: number) => void, loading?: boolean }} props
 */
export default function TablePagination({
  page,
  pages,
  total,
  limit,
  onPageChange,
  onLimitChange,
  loading = false,
}) {
  if (total === 0 && !loading) return null;

  const safePages = Math.max(pages, 1);
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-zinc-100 text-sm"
      data-testid="table-pagination"
    >
      <div className="text-xs text-zinc-500 tabular-nums">
        {total === 0 ? "No results" : (
          <>
            Showing <span className="text-zinc-700 font-medium">{start}–{end}</span> of{" "}
            <span className="text-zinc-700 font-medium">{total}</span>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {onLimitChange && (
          <label className="flex items-center gap-1.5 text-xs text-zinc-500">
            Per page
            <select
              data-testid="pagination-page-size"
              value={limit}
              disabled={loading}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="h-8 px-2 border border-zinc-200 rounded-sm bg-white text-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}

        <button
          type="button"
          data-testid="pagination-prev"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="h-8 px-2.5 inline-flex items-center gap-1 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
        >
          <CaretLeft size={14} weight="bold" />
          Previous
        </button>

        <span className="text-xs text-zinc-600 tabular-nums min-w-[4.5rem] text-center">
          Page {page} of {safePages}
        </span>

        <button
          type="button"
          data-testid="pagination-next"
          disabled={loading || page >= safePages || total === 0}
          onClick={() => onPageChange(page + 1)}
          className="h-8 px-2.5 inline-flex items-center gap-1 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
        >
          Next
          <CaretRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
